import { Badge } from "@bowfast/ui/components/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Link } from "@tanstack/react-router";
import { ArrowRightIcon, CalendarIcon, CompassIcon, MapPinIcon, Navigation2Icon, SailboatIcon } from "lucide-react";
import { TYPE_CONFIG } from "~/pages/routes/routes.config";
import type { RouteCardRoute } from "~/pages/routes/routes.types";
import { MiniRouteChart } from "./MiniRouteChart";

// ── RouteCard ─────────────────────────────────────────────────────────────────

export interface RouteCardProps {
  route: RouteCardRoute;
}

export function RouteCard({ route }: Readonly<RouteCardProps>) {
  const cfg = TYPE_CONFIG[route.type];
  const TypeIcon = cfg.Icon;

  return (
    <Link
      to="/routes/$routeId"
      params={{ routeId: route.id }}
      className="group block outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-xl"
    >
      <Card className="pt-0 transition-all hover:shadow-lg hover:ring-abyss-300/40">
        {/* Chart header — dark ocean canvas, flush to card top */}
        <div className="relative h-32 bg-linear-to-br from-[#060b16] to-[#0d2040] overflow-hidden rounded-t-xl">
          <MiniRouteChart points={route.chartPoints} lineColor={cfg.lineColor} glowColor={cfg.glowColor} />
          {/* Route type badge — top left */}
          <div className="absolute top-3 left-3 z-10">
            <Badge variant={cfg.variant} className="text-[0.625rem] backdrop-blur-sm">
              <TypeIcon className="size-3" data-icon="inline-start" />
              {cfg.label}
            </Badge>
          </div>
          {/* Waypoint count — top right */}
          <div className="absolute top-3 right-3 z-10">
            <span className="flex items-center gap-1 rounded-full bg-black/40 backdrop-blur-sm px-2 py-0.5 text-[0.625rem] font-semibold text-white/70">
              <MapPinIcon className="size-2.5" />
              {route.waypointCount} waypoints
            </span>
          </div>
          {/* Bottom fade */}
          <div className="absolute inset-x-0 bottom-0 h-10 bg-linear-to-t from-[#060b16]/90 to-transparent" />
        </div>

        {/* Header: name + ports */}
        <CardHeader>
          <CardTitle className="font-heading text-[0.9375rem] font-semibold leading-tight tracking-tight group-hover:text-abyss-500 transition-colors">
            {route.name}
          </CardTitle>
          <CardDescription className="flex items-center gap-1.5 text-xs">
            <MapPinIcon className="size-3 shrink-0 text-shallow-500" />
            <span className="truncate">{route.departurePort}</span>
            <ArrowRightIcon className="size-3 shrink-0 text-muted-foreground/40" />
            <MapPinIcon className="size-3 shrink-0 text-signal-500" />
            <span className="truncate">{route.arrivalPort}</span>
          </CardDescription>
        </CardHeader>

        {/* Content: stats row */}
        <CardContent>
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <Navigation2Icon className="size-3 text-abyss-400" />
              <span className="font-semibold tabular-nums">{route.distance.toLocaleString()}</span>
              <span className="text-muted-foreground">nm</span>
            </div>
            <div className="h-3 w-px bg-border" />
            <div className="flex items-center gap-1">
              <CompassIcon className="size-3 text-shallow-500" />
              <span className="font-medium">{route.estimatedDuration}</span>
            </div>
            {route.expeditionCount > 0 && (
              <>
                <div className="h-3 w-px bg-border" />
                <div className="flex items-center gap-1">
                  <SailboatIcon className="size-3 text-signal-500" />
                  <span className="font-medium tabular-nums">{route.expeditionCount}</span>
                  <span className="text-muted-foreground">
                    {route.expeditionCount === 1 ? "expedition" : "expeditions"}
                  </span>
                </div>
              </>
            )}
          </div>
        </CardContent>

        {/* Footer: last sailed / created */}
        <CardFooter className="flex items-center justify-between text-[0.6875rem] text-muted-foreground">
          <span className="flex items-center gap-1">
            <CalendarIcon className="size-3" />
            {route.lastUsed ? `Last sailed ${route.lastUsed}` : `Created ${route.createdAt}`}
          </span>
          <ArrowRightIcon className="size-3.5 text-muted-foreground/30 transition-transform group-hover:translate-x-0.5 group-hover:text-abyss-500" />
        </CardFooter>
      </Card>
    </Link>
  );
}
